import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getTemplates, incrementTemplateUsage } from '../../services/api';
import { BACKEND_URL } from '../../config/api';
import { TEMPLATE_DISCLAIMER } from '../../constants/disclaimers';
import { getImageUrl } from '../../utils/imageUrl';
import {
  Search,
  Filter,
  Copy,
  Check,
  X,
  Lock,
  TrendingUp,
  Activity,
  BarChart3,
  LineChart,
  Volume2,
  Grid3x3,
  Zap,
  Code,
  Image as ImageIcon,
  AlertTriangle,
  ChevronDown,
  Menu,
  FileCode,
  Layers,
  ArrowLeft
} from 'lucide-react';
import './Templates.css';

const Templates = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedSubcategory, setSelectedSubcategory] = useState('All');
  const [categories, setCategories] = useState(['All']);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [expandedCategories, setExpandedCategories] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Category icons mapping
  const getCategoryIcon = (category) => {
    const iconMap = {
      'Moving Averages': TrendingUp,
      'Oscillators': Activity,
      'Volatility': BarChart3,
      'Trend': LineChart,
      'Volume': Volume2,
      'Support/Resistance': Grid3x3,
      'Patterns': Zap,
      'Other': Code
    };
    return iconMap[category] || Code;
  };
  // Category colors mapping matching the icon/theme
  const getCategoryColor = (category) => {
    const colorMap = {
      'Moving Averages': 'bg-blue-500',
      'Oscillators': 'bg-purple-500',
      'Volatility': 'bg-orange-500',
      'Trend': 'bg-green-500',
      'Volume': 'bg-pink-500',
      'Support/Resistance': 'bg-indigo-500',
      'Patterns': 'bg-yellow-500',
      'Other': 'bg-gray-500'
    };
    return colorMap[category] || 'bg-blue-500';
  };

  useEffect(() => {
    fetchCategories();
    fetchTemplates();
  }, []);

  useEffect(() => {
    filterTemplates();
  }, [templates, searchQuery, selectedCategory, selectedSubcategory]);

  useEffect(() => {
    if (selectedCategory && selectedCategory !== 'All') {
      fetchSubcategories(selectedCategory);
    } else {
      setSubcategories([]);
      setSelectedSubcategory('All');
    }
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${BACKEND_URL}/api/v1/admin/templates/categories`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        credentials: 'include'
      });

      const data = await response.json();
      if (data.success) {
        setCategories(['All', ...(data.data.all || [])]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Fallback to predefined categories
      setCategories([
        'All',
        'Moving Averages',
        'Oscillators',
        'Volatility',
        'Trend',
        'Volume',
        'Support/Resistance',
        'Patterns',
        'Other'
      ]);
    }
  };

  const fetchSubcategories = async (categoryName) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${BACKEND_URL}/api/v1/admin/templates/categories/${encodeURIComponent(categoryName)}/subcategories`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        credentials: 'include'
      });

      const data = await response.json();
      if (data.success) {
        setSubcategories(data.data.subcategories || []);
      } else {
        setSubcategories([]);
      }
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      setSubcategories([]);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await getTemplates();
      console.log('📥 Templates API Response:', response.data);
      console.log('👤 User Tier:', user?.subscription?.tier || user?.tier || 'free');
      console.log('📋 Templates received:', response.data.data);
      setTemplates(response.data.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      showToast('Failed to fetch templates', 'error');
    } finally {
      setLoading(false);
    }
  };

  const filterTemplates = () => {
    let filtered = [...templates];

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(template =>
        template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.subcategory?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by category
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(template => template.category === selectedCategory);
    }

    // Filter by subcategory
    if (selectedSubcategory !== 'All') {
      filtered = filtered.filter(template => template.subcategory === selectedSubcategory);
    }

    setFilteredTemplates(filtered);
  };

  // Group templates by category and subcategory
  const groupTemplatesByCategory = () => {
    const grouped = {};

    filteredTemplates.forEach(template => {
      const category = template.category || 'Other';

      if (!grouped[category]) {
        grouped[category] = {
          templates: [],
          subcategories: {}
        };
      }

      // If template has subcategory, group by subcategory within category
      if (template.subcategory) {
        const subcategory = template.subcategory;
        if (!grouped[category].subcategories[subcategory]) {
          grouped[category].subcategories[subcategory] = [];
        }
        grouped[category].subcategories[subcategory].push(template);
      } else {
        // No subcategory, add directly to category
        grouped[category].templates.push(template);
      }
    });

    return grouped;
  };

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Handle sidebar category selection
  const handleSidebarCategoryClick = (category) => {
    setSelectedCategory(category);
    setSelectedSubcategory('All');
    // Close sidebar on mobile after selection
    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  };

  // Handle back button - navigate to dashboard
  const handleBackToMain = () => {
    const dashboardRoute = user?.role === 'admin'
      ? '/admin/dashboard'
      : user?.role === 'instructor'
        ? '/instructor/dashboard'
        : '/student/dashboard';
    navigate(dashboardRoute);
  };

  // Initialize all categories as expanded on first load - DISABLED by user request
  // useEffect(() => {
  //   if (filteredTemplates.length > 0 && Object.keys(expandedCategories).length === 0) {
  //     const grouped = groupTemplatesByCategory();
  //     const initialExpanded = {};
  //     Object.keys(grouped).forEach(category => {
  //       initialExpanded[category] = true; // All expanded by default
  //     });
  //     setExpandedCategories(initialExpanded);
  //   }
  // }, [filteredTemplates]);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 2000);
  };

  const handleCopyCode = async (template) => {
    try {
      await navigator.clipboard.writeText(template.code);
      setCopiedId(template._id);
      showToast('Code copied to clipboard!', 'success');

      // Increment usage count
      await incrementTemplateUsage(template._id);

      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error('Error copying code:', error);
      showToast('Failed to copy code', 'error');
    }
  };

  const handleTemplateClick = (template) => {
    console.log('🖱️ Template clicked:', template.name);
    console.log('📝 Template data:', template);
    console.log('💻 Code field:', template.code);
    console.log('📏 Code length:', template.code?.length || 0);

    const isAccessible = canAccessTemplate(template.visibilityTiers);
    if (!isAccessible) {
      return; // Don't open modal for locked templates
    }
    setSelectedTemplate(template);
    setShowDetailModal(true);
  };

  const handleCloseModal = () => {
    setShowDetailModal(false);
    setSelectedTemplate(null);
  };

  const handleUpgrade = () => {
    window.location.href = '/payment/checkout';
  };

  const handleImageClick = (imageUrl, e) => {
    if (e) e.stopPropagation();
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  };

  const getTierBadgeClass = (tier) => {
    const classMap = {
      'free': 'tier-badge-free',
      'vip': 'tier-badge-vip',
      'premium': 'tier-badge-premium'
    };
    return classMap[tier] || 'tier-badge-free';
  };

  const getTierIcon = (tier) => {
    const iconMap = {
      'free': '👤',
      'vip': '⭐',
      'premium': '👑'
    };
    return iconMap[tier] || '👤';
  };

  const getHighestTier = (tiers) => {
    if (tiers.includes('premium')) return 'premium';
    if (tiers.includes('vip')) return 'vip';
    return 'free';
  };

  // Convert YouTube URL to embed format
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;

    // Handle different YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/,
      /youtube\.com\/embed\/([^&\s]+)/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return `https://www.youtube.com/embed/${match[1]}`;
      }
    }

    return null;
  };

  // Determine if user can access template based on tier hierarchy
  const canAccessTemplate = (templateTiers) => {
    // Get user's subscription tier - check multiple possible fields
    const userTier = user?.subscription?.tier || user?.tier || 'free';

    // Premium users can access everything
    if (userTier === 'premium') return true;

    // VIP users can access VIP and free templates
    if (userTier === 'vip') {
      return templateTiers.includes('vip') || templateTiers.includes('free');
    }

    // Free users can only access free templates
    return templateTiers.includes('free');
  };

  // Get the LOWEST tier user needs to upgrade to for locked template
  const getRequiredUpgradeTier = (templateTiers) => {
    const userTier = user?.subscription?.tier || user?.tier || 'free';

    // For FREE users: Find the lowest tier that gives access
    if (userTier === 'free') {
      // If VIP is available, suggest VIP (lowest paid tier)
      if (templateTiers.includes('vip')) {
        return 'vip';
      }
      // Otherwise, suggest premium
      if (templateTiers.includes('premium')) {
        return 'premium';
      }
    }

    // For VIP users: They can only upgrade to premium
    if (userTier === 'vip') {
      if (templateTiers.includes('premium') && !templateTiers.includes('vip')) {
        return 'premium';
      }
    }

    return 'premium'; // Default fallback
  };

  return (
    <div className="templates-page-container">
      {/* Sidebar Overlay for Mobile */}
      {sidebarOpen && (
        <div
          className="templates-sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar Navigation */}
      <aside className={`templates-sidebar ${sidebarOpen ? 'open' : ''}`}>
        {/* Back Button */}
        <button
          className="templates-back-btn"
          onClick={handleBackToMain}
          aria-label="Back to main menu"
        >
          <ArrowLeft size={20} />
          <span>Back to Main Menu</span>
        </button>

        {/* Sidebar Header */}
        <div className="templates-sidebar-header">
          <div className="templates-sidebar-title">
            <FileCode size={24} className="templates-sidebar-icon" />
            <div>
              <h2>Code Templates</h2>
              <p>Browse by category</p>
            </div>
          </div>
          <button
            className="templates-sidebar-close"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close sidebar"
          >
            <X size={20} />
          </button>
        </div>

        {/* Category List */}
        <nav className="templates-sidebar-nav">
          <div className="templates-sidebar-section">
            <div className="templates-sidebar-section-title">
              <Layers size={16} />
              <span>Categories</span>
            </div>

            {/* All Templates */}
            <button
              className={`templates-sidebar-item ${selectedCategory === 'All' ? 'active' : ''}`}
              onClick={() => handleSidebarCategoryClick('All')}
            >
              <Code size={18} />
              <span>All Templates</span>
              <span className="templates-sidebar-count">{templates.length}</span>
            </button>

            {/* Dynamic Categories */}
            {categories
              .filter(cat => cat !== 'All')
              .map(category => {
                const CategoryIcon = getCategoryIcon(category);
                const count = templates.filter(t => t.category === category).length;

                return (
                  <button
                    key={category}
                    className={`templates-sidebar-item ${selectedCategory === category ? 'active' : ''}`}
                    onClick={() => handleSidebarCategoryClick(category)}
                  >
                    <CategoryIcon size={18} />
                    <span>{category}</span>
                    <span className="templates-sidebar-count">{count}</span>
                  </button>
                );
              })}
          </div>
        </nav>
      </aside>

      {/* Main Content */}
      <div className="templates-page">
        {/* Mobile Menu Toggle */}
        <button
          className="templates-mobile-menu-btn"
          onClick={() => setSidebarOpen(true)}
          aria-label="Open categories"
        >
          <Menu size={24} />
          <span>Categories</span>
        </button>

        {/* Header Section */}
        <div className="templates-header">
          <div className="header-content">
            <div className="header-title">
              <Code size={32} className="header-icon" />
              <div>
                <h1>Code Templates</h1>
                <p>Ready-to-use Pine Script indicators</p>
              </div>
            </div>
            <div className="user-tier-badge">
              <span className={getTierBadgeClass(user?.subscription?.tier || user?.tier || 'free')}>
                {getTierIcon(user?.subscription?.tier || user?.tier || 'free')} {(user?.subscription?.tier || user?.tier || 'free').toUpperCase()}
              </span>
            </div>
          </div>

          {/* Search and Filter Bar */}
          <div className="search-filter-bar">
            <div className="search-box">
              <Search size={20} className="search-icon" />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="category-filter">
              <Filter size={20} className="filter-icon" />
              <select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setSelectedSubcategory('All');
                }}
                className="category-select"
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category === 'All' ? 'All Categories' : category}
                  </option>
                ))}
              </select>
            </div>

            {/* Subcategory Filter - Show only when a category is selected */}
            {selectedCategory !== 'All' && subcategories.length > 0 && (
              <div className="category-filter">
                <Filter size={20} className="filter-icon" />
                <select
                  value={selectedSubcategory}
                  onChange={(e) => setSelectedSubcategory(e.target.value)}
                  className="category-select"
                >
                  <option value="All">All Subcategories</option>
                  {subcategories.map(subcategory => (
                    <option key={subcategory} value={subcategory}>
                      {subcategory}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Templates Grid */}
        <div className="templates-content">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading templates...</p>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="empty-state">
              <Code size={64} className="empty-icon" />
              <h3>No templates found</h3>
              <p>Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {Object.entries(groupTemplatesByCategory()).map(([category, categoryData]) => {
                const CategoryIcon = getCategoryIcon(category);
                const CategoryColor = getCategoryColor(category);
                const isExpanded = expandedCategories[category];
                const hasSubcategories = Object.keys(categoryData.subcategories).length > 0;
                const directTemplates = categoryData.templates;
                const totalTemplates = directTemplates.length +
                  Object.values(categoryData.subcategories).reduce((sum, templates) => sum + templates.length, 0);

                return (
                  <div key={category} className="rounded-xl overflow-hidden">
                    {/* Category Header Styled like Community Card */}
                    <div
                      onClick={() => toggleCategory(category)}
                      className="relative flex items-center justify-between p-4 md:p-5 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-black hover:border-blue-300 dark:hover:border-blue-700 cursor-pointer transition-all duration-200 group shadow-sm hover:shadow-md"
                    >
                      <div className="flex items-center">
                        <div className={`relative p-2.5 md:p-3 rounded-lg mr-3 md:mr-4 ${CategoryColor} text-white shadow-sm`}>
                          <CategoryIcon size={20} />
                        </div>
                        <div>
                          <h3 className="font-bold text-sm md:text-base text-gray-800 dark:text-gray-100 flex items-center gap-2">
                            {category}
                            <span className="text-xs font-normal text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">
                              {totalTemplates}
                            </span>
                          </h3>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {hasSubcategories ? `${Object.keys(categoryData.subcategories).length} Subcategories` : 'Direct Templates'}
                          </p>
                        </div>
                      </div>
                      <ChevronDown
                        size={20}
                        className={`text-gray-400 group-hover:text-blue-500 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                      />
                    </div>

                    {/* Category Content */}
                    {isExpanded && (
                      <div className="category-content">
                        {/* Direct templates (no subcategory) */}
                        {directTemplates.length > 0 && (
                          <div className="templates-grid">
                            {directTemplates.map(template => {
                              const highestTier = getHighestTier(template.visibilityTiers);
                              const isAccessible = canAccessTemplate(template.visibilityTiers);
                              const requiredTier = getRequiredUpgradeTier(template.visibilityTiers);
                              const IconComponent = getCategoryIcon(template.category);

                              return (
                                <div
                                  key={template._id}
                                  className={`template-card ${!isAccessible ? 'locked' : ''}`}
                                  onClick={() => handleTemplateClick(template)}
                                >
                                  {!isAccessible && (
                                    <div className="locked-overlay">
                                      <Lock size={32} />
                                      <p className="locked-text">Upgrade to {requiredTier.toUpperCase()}</p>
                                      <button
                                        className="btn-upgrade"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleUpgrade();
                                        }}
                                      >
                                        Upgrade to {requiredTier.toUpperCase()}
                                      </button>
                                    </div>
                                  )}

                                  {/* Template Image */}
                                  {template.imageUrl ? (
                                    <div
                                      className="template-image"
                                      onClick={(e) => isAccessible && handleImageClick(template.imageUrl, e)}
                                      style={{ cursor: isAccessible ? 'pointer' : 'default' }}
                                    >
                                      <img
                                        src={getImageUrl(template.imageUrl)}
                                        alt={template.name}
                                        onError={(e) => {
                                          console.warn('Image failed to load:', getImageUrl(template.imageUrl));
                                          e.target.onerror = null;
                                          e.target.style.opacity = '0.3';
                                        }}
                                      />
                                    </div>
                                  ) : (
                                    <div className="template-image-placeholder">
                                      <ImageIcon size={32} />
                                    </div>
                                  )}

                                  <div className="card-header">
                                    <div className="category-badge">
                                      <IconComponent size={16} />
                                      <span>{template.category}{template.subcategory ? ` • ${template.subcategory}` : ''}</span>
                                    </div>
                                    <span className={`tier-badge ${getTierBadgeClass(highestTier)}`}>
                                      {getTierIcon(highestTier)}
                                    </span>
                                  </div>

                                  <h3 className="template-name">{template.name}</h3>

                                  <p className="template-description">
                                    {template.description || 'No description available'}
                                  </p>

                                  {isAccessible && (
                                    <button
                                      className="btn-copy"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCopyCode(template);
                                      }}
                                    >
                                      {copiedId === template._id ? (
                                        <>
                                          <Check size={18} />
                                          Copied!
                                        </>
                                      ) : (
                                        <>
                                          <Copy size={18} />
                                          Copy Code
                                        </>
                                      )}
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Subcategory sections */}
                        {hasSubcategories && Object.entries(categoryData.subcategories).map(([subcategory, templates]) => (
                          <div key={subcategory} className="subcategory-section">
                            <h3 className="subcategory-title">{subcategory}</h3>
                            <div className="templates-grid">
                              {templates.map(template => {
                                const highestTier = getHighestTier(template.visibilityTiers);
                                const isAccessible = canAccessTemplate(template.visibilityTiers);
                                const requiredTier = getRequiredUpgradeTier(template.visibilityTiers);
                                const IconComponent = getCategoryIcon(template.category);

                                return (
                                  <div
                                    key={template._id}
                                    className={`template-card ${!isAccessible ? 'locked' : ''}`}
                                    onClick={() => handleTemplateClick(template)}
                                  >
                                    {!isAccessible && (
                                      <div className="locked-overlay">
                                        <Lock size={32} />
                                        <p className="locked-text">Upgrade to {requiredTier.toUpperCase()}</p>
                                        <button
                                          className="btn-upgrade"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleUpgrade();
                                          }}
                                        >
                                          Upgrade to {requiredTier.toUpperCase()}
                                        </button>
                                      </div>
                                    )}

                                    {/* Template Image */}
                                    {template.imageUrl ? (
                                      <div
                                        className="template-image"
                                        onClick={(e) => isAccessible && handleImageClick(template.imageUrl, e)}
                                        style={{ cursor: isAccessible ? 'pointer' : 'default' }}
                                      >
                                        <img
                                          src={getImageUrl(template.imageUrl)}
                                          alt={template.name}
                                          onError={(e) => {
                                            console.warn('Image failed to load:', getImageUrl(template.imageUrl));
                                            e.target.onerror = null;
                                            e.target.style.opacity = '0.3';
                                          }}
                                        />
                                      </div>
                                    ) : (
                                      <div className="template-image-placeholder">
                                        <ImageIcon size={32} />
                                      </div>
                                    )}

                                    <div className="card-header">
                                      <div className="category-badge">
                                        <IconComponent size={16} />
                                        <span>{template.category}{template.subcategory ? ` • ${template.subcategory}` : ''}</span>
                                      </div>
                                      <span className={`tier-badge ${getTierBadgeClass(highestTier)}`}>
                                        {getTierIcon(highestTier)}
                                      </span>
                                    </div>

                                    <h3 className="template-name">{template.name}</h3>

                                    <p className="template-description">
                                      {template.description || 'No description available'}
                                    </p>

                                    {isAccessible && (
                                      <button
                                        className="btn-copy"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleCopyCode(template);
                                        }}
                                      >
                                        {copiedId === template._id ? (
                                          <>
                                            <Check size={18} />
                                            Copied!
                                          </>
                                        ) : (
                                          <>
                                            <Copy size={18} />
                                            Copy Code
                                          </>
                                        )}
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Template Detail Modal */}
        {showDetailModal && selectedTemplate && (
          <div className="modal-overlay" onClick={handleCloseModal}>
            <div className="modal-content template-detail-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <h2>{selectedTemplate.name}</h2>
                  <div className="modal-meta">
                    <span className="category-tag">
                      {React.createElement(getCategoryIcon(selectedTemplate.category), { size: 16 })}
                      {selectedTemplate.category}
                    </span>
                    {selectedTemplate.subcategory && (
                      <span className="category-tag">
                        {selectedTemplate.subcategory}
                      </span>
                    )}
                    <span className={`tier-badge ${getTierBadgeClass(getHighestTier(selectedTemplate.visibilityTiers))}`}>
                      {getTierIcon(getHighestTier(selectedTemplate.visibilityTiers))} {getHighestTier(selectedTemplate.visibilityTiers).toUpperCase()}
                    </span>
                  </div>
                </div>
                <button className="btn-close" onClick={handleCloseModal}>
                  <X size={24} />
                </button>
              </div>

              <div className="modal-body">
                {selectedTemplate.imageUrl && (
                  <div className="modal-image-section">
                    <img
                      src={getImageUrl(selectedTemplate.imageUrl)}
                      alt={selectedTemplate.name}
                      className="modal-template-image"
                      onClick={(e) => handleImageClick(selectedTemplate.imageUrl, e)}
                      style={{ cursor: 'pointer' }}
                      title="Click to view full size"
                      onError={(e) => {
                        console.warn('Modal image failed to load:', getImageUrl(selectedTemplate.imageUrl));
                        e.target.onerror = null; // Prevent infinite loop
                      }}
                    />
                  </div>
                )}

                {selectedTemplate.description && (
                  <div className="template-description-full">
                    <h4>Description</h4>
                    <p>{selectedTemplate.description}</p>
                  </div>
                )}

                {/* Ebook Link Button */}
                {selectedTemplate.ebookUrl && (
                  <div className="template-ebook-section" style={{ marginTop: '20px', marginBottom: '20px' }}>
                    <a
                      href={selectedTemplate.ebookUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-copy"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', textDecoration: 'none' }}
                    >
                      📚 View Tutorial PDF
                    </a>
                  </div>
                )}

                {/* YouTube Embedded Player */}
                {selectedTemplate.youtubeUrl && getYouTubeEmbedUrl(selectedTemplate.youtubeUrl) && (
                  <div className="template-youtube-section" style={{ marginTop: '20px', marginBottom: '20px' }}>
                    <h4 style={{ marginBottom: '12px' }}>Tutorial Video</h4>
                    <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: '8px' }}>
                      <iframe
                        src={getYouTubeEmbedUrl(selectedTemplate.youtubeUrl)}
                        title="Tutorial Video"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%'
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Disclaimer Section - Displayed for all templates */}
                <div className="template-disclaimer">
                  <div className="disclaimer-header">
                    <AlertTriangle size={20} className="disclaimer-icon" />
                    <h4>Disclaimer / Penafian</h4>
                  </div>
                  <div className="disclaimer-content">
                    {TEMPLATE_DISCLAIMER.split('\n\n').map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </div>
                </div>

                <div className="template-code-section">
                  <div className="code-header">
                    <h4>Pine Script Code</h4>
                    <button
                      className="btn-copy-modal"
                      onClick={() => handleCopyCode(selectedTemplate)}
                    >
                      {copiedId === selectedTemplate._id ? (
                        <>
                          <Check size={18} />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy size={18} />
                          Copy Code
                        </>
                      )}
                    </button>
                  </div>
                  {selectedTemplate.code ? (
                    <pre className="code-preview">{selectedTemplate.code}</pre>
                  ) : (
                    <div className="code-empty-state">
                      <Code size={48} />
                      <p>No code available for this template</p>
                      <p className="debug-info">Debug: code field is {typeof selectedTemplate.code}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Full-Size Image Modal */}
        {showImageModal && selectedImage && (
          <div className="image-modal-overlay" onClick={() => setShowImageModal(false)}>
            <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="btn-close-image-modal" onClick={() => setShowImageModal(false)}>
                <X size={32} />
              </button>
              <img
                src={`${BACKEND_URL}${selectedImage}`}
                alt="Full size preview"
                className="full-size-template-image"
                onError={(e) => {
                  console.warn('Full-size image failed to load:', `${BACKEND_URL}${selectedImage}`);
                  e.target.onerror = null; // Prevent infinite loop
                }}
              />
            </div>
          </div>
        )}

        {/* Toast Notification */}
        {toast.show && (
          <div className={`toast toast-${toast.type}`}>
            {toast.type === 'success' ? <Check size={20} /> : <X size={20} />}
            {toast.message}
          </div>
        )}
      </div>
    </div>
  );
};

export default Templates;
